import { i18nMark } from '@lingui/react'

// todo check all utils, add tests where missed

import {
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
  PRODUCT_TEMPLATES
} from 'shared/consts/productTemplates'
import {
  PRODUCT_STATUS_AV,
  PRODUCT_STATUS_AVAILABLE,
  PRODUCT_STATUS_GRATIS,
  PRODUCT_STATUS_NL
} from 'shared/consts/stockStatus'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import { shouldRenderLabel } from 'views/utils/redPointsUtils'
import { TENANT_FR } from 'shared/consts/tenants'

/**
 * Determine whether products search result is valid
 *
 * @param product
 * @returns {boolean}
 */
export const isValidProduct = product =>
  Boolean(product.id) &&
  Boolean(product.name) &&
  Boolean(product.thumbnailURL) &&
  Boolean(product.prices)

export const getDocumentTypes = () => ({
  externalProductPDF: i18nMark('product.link.label.packageInsert'),
  internalProductPDF: i18nMark('product.link.label.packageInsert'),
  externalLMIVPDF: i18nMark('product.link.label.foodInformation'),
  internalLMIVPDF: i18nMark('product.link.label.foodInformation'),
  importantPharmaceuticalNotices: i18nMark(
    'product.link.label.pharmaceuticalNotices'
  )
})

export const getDocuments = product => {
  if (product.documents && product.documents.length > 0) {
    const DOC_TYPES = getDocumentTypes()
    return product.documents
      .filter(doc => DOC_TYPES[doc.doctype])
      .map(doc => ({ link: doc.link, text: DOC_TYPES[doc.doctype] }))
  }

  return []
}

export const isProductAvailable = ({ status } = {}) =>
  status === PRODUCT_STATUS_AVAILABLE

export const isProductOutOfTrade = ({ status }) => status === PRODUCT_STATUS_AV

export const isProductOutOfStock = ({ status }) => status === PRODUCT_STATUS_NL

export const isProductFree = ({ status }) => status === PRODUCT_STATUS_GRATIS

export const isVariantVisible = (currentId, { id: variantId, stockStatus }) =>
  variantId === currentId ||
  isProductAvailable(stockStatus) ||
  isProductOutOfStock(stockStatus)

// todo extract promotion types into consts
export const isValidPromotion = (
  promotion,
  { code, headline, description, packshot } = {}
) =>
  ['oneforone', 'bundle', 'freebie'].indexOf(promotion) > -1 &&
  Boolean(promotion !== 'bundle' && code && headline && description && packshot)

export const calculateDiscountPercents = ({
  retailPrice,
  listPrice,
  locale
}) => {
  const rounded = 100 - (retailPrice / listPrice) * 100

  return rounded.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
}

export const getRealPrice = ({ prescriptionType, variant }) =>
  getPriceForType(
    variant.prices,
    PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionType]
  )

export const getListPriceSuperScript = ({ superScripts, pharmaObl }) => {
  const { avp, avpPharmaObl } = superScripts

  return pharmaObl && avpPharmaObl ? avpPharmaObl : avp
}

export const isAllProductVariantsNL = (
  stockStatusOfSelectedVariant,
  variants
) => {
  // If the selected variant is AV, return false
  if (stockStatusOfSelectedVariant.status === PRODUCT_STATUS_AV) return false

  if (variants) {
    // Remove AV products, and check if there are still products
    const visibleVariants = variants.filter(
      ({ stockStatus }) => stockStatus.status !== PRODUCT_STATUS_AV
    )
    if (visibleVariants.length === 0) return false

    // then check if they are all NL
    return (
      variants.filter(
        ({ stockStatus }) => stockStatus.status === PRODUCT_STATUS_NL
      ).length === visibleVariants.length
    )
  }

  return false
}

export const isPricePresent = price =>
  price && typeof price.amount !== 'undefined' && price.amount > 0

export const isListPriceGreaterThanOrEqualToRetailPrice = ({
  listPrice,
  retailPrice
}) =>
  isPricePresent(listPrice) &&
  isPricePresent(retailPrice) &&
  listPrice.amount > retailPrice.amount

export const isDiscountValid = (discount, discountBoundaries) => {
  if (!discount || Number.isNaN(discount) || discount <= 0) {
    return false
  }

  if (discountBoundaries) {
    const { min, max } = discountBoundaries

    if (min && max && (discount < min || discount > max)) {
      return false
    }
  }

  return true
}

// todo: when the experiment is approved or not, keep the util and add tests here
export const getProductLabelsProps = ({
  product,
  productTemplate,
  displayNowWidget,
  prescriptionRequiredLabel
}) => {
  const {
    promotion,
    promotionProduct,
    labels,
    stockStatus,
    multiplierRP,
    redPoints,
    redPointsIsActive,
    redPointsMultiplierIsValid,
    pharmaObl
  } = product

  const productLabelsProps = {
    stockStatus,
    displayNowWidget,
    pharmaObl
  }

  if (
    [PRODUCT_TEMPLATES.OTC, PRODUCT_TEMPLATES.OTX].includes(productTemplate)
  ) {
    productLabelsProps.promotion = promotion
    productLabelsProps.promotionProduct = promotionProduct
    productLabelsProps.labels = labels
    productLabelsProps.redPointsData = {
      productTemplate,
      redPointsMultiplierIsValid,
      redPointsIsActive,
      redPoints,
      multiplierRP
    }
  } else {
    productLabelsProps.prescription = true
    productLabelsProps.labels = labels.filter(
      label => label !== prescriptionRequiredLabel
    )
  }

  return productLabelsProps
}

// todo: when the experiment is approved or not, keep the util and add tests here
export const shouldDisplayProductLabels = ({
  product,
  productTemplate,
  displayNowWidget
}) => {
  const {
    promotion,
    labels,
    stockStatus,
    multiplierRP,
    redPoints,
    redPointsIsActive,
    redPointsMultiplierIsValid
  } = product

  const isOutOfTrade = stockStatus.status === PRODUCT_STATUS_AV
  const hasLabels = labels && labels.length > 0
  const shouldRenderRedPointsLabel = shouldRenderLabel({
    productTemplate,
    redPointsMultiplierIsValid,
    redPointsIsActive,
    redPoints,
    multiplierRP
  })

  return (
    hasLabels ||
    displayNowWidget ||
    promotion ||
    isOutOfTrade ||
    shouldRenderRedPointsLabel
  )
}

export const shouldRenderRatings = ({
  tenant,
  productTemplate,
  medicament
}) => {
  const isRx = productTemplate === PRODUCT_TEMPLATES.RX

  if (isRx) {
    return false
  }

  if (tenant !== TENANT_FR) {
    return true
  }

  return !(medicament && productTemplate === PRODUCT_TEMPLATES.OTC)
}

export const hasFlag = (productFlags, exceptionFlags) =>
  exceptionFlags.some(flag => Boolean(productFlags[flag]))
